<template>
    <div id="ChatbotAiPrompt">
        <div>
            <h1>{{ $t("chatbotv2.aiPrompt") }}</h1>
            <p>
                {{ $t("message.chatbotAiPromptDesc") }}
            </p>
            <a-textarea
                v-model="promptText"
                :placeholder="$t('message.enterPrompt')"
                :auto-size="{ minRows: 2 }"
                style="min-height: 200px"
            />
        </div>
        <hr />
        <div class="parameter-tuning" :key="key">
            <h1>{{ $t("message.aiParameterTunings") }}</h1>
            <p>
                {{ $t("message.aiParameterTuningsDesc") }}
            </p>
            <div>
                <FeatureItem
                    :title="$t('message.temperature')"
                    icon="ChatAiIcon"
                    v-model="temperature"
                    :description="$t('message.temperatureDesc')"
                    type="number"
                    :min="0.01"
                    :max="2"
                    :step="0.1"
                    v-if="story.ai"
                    number-format="float"
                    @blur="changeTemperature(temperature)"
                    @increase="changeTemperature(temperature)"
                    @decrease="changeTemperature(temperature)"
                />
            </div>

            <div>
                <FeatureItem
                    title="Top-P"
                    icon="ChatAiIcon"
                    v-model="topP"
                    :description="$t('message.topDesc')"
                    type="number"
                    :min="0.01"
                    :max="1"
                    :step="0.1"
                    v-if="story.ai"
                    number-format="float"
                    @blur="changeTopP(topP)"
                    @increase="changeTopP(topP)"
                    @decrease="changeTopP(topP)"
                />
            </div>

            <div>
                <FeatureItem
                    :title="$t('chatbotv2.aiListenerDelay')"
                    icon="ChatAiIcon"
                    v-model="aiListenerTimeout"
                    :description="$t('chatbotv2.aiListenerDelayDesc')"
                    type="number"
                    :min="5000"
                    :max="60000"
                    :step="1000"
                    v-if="story.ai"
                    number-format="integer"
                />
            </div>

            <div class="modes">
                <strong> {{ $t("message.toolChoices") }} </strong>
                <div class="mode-items" :key="key">
                    <FeatureItem
                        :title="$t('whatsapp_business_api.none')"
                        icon="ChatAiIcon"
                        v-model="toolChoiceNone"
                        :description="$t('message.noneDesc')"
                        type="switch"
                        v-if="story.ai"
                    />

                    <FeatureItem
                        :title="$t('chatbotv2.auto')"
                        icon="ChatAiIcon"
                        v-model="toolChoiceAuto"
                        :description="$t('message.autoDesc')"
                        type="switch"
                        v-if="story.ai"
                    />

                    <FeatureItem
                        :title="$t('message.required')"
                        icon="ChatAiIcon"
                        v-model="toolChoiceRequired"
                        :description="$t('message.requiredDesc')"
                        type="switch"
                        v-if="story.ai"
                    />
                </div>
            </div>

        </div>
        <div class="settings-btn">
            <SupsisButton ghost @click="clearPrompt"> {{ $t("message.cancel") }}</SupsisButton>
            <SupsisButton primary @click="updatePrompt">{{ $t("message.save") }}</SupsisButton>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import CommingSoonData from "../ComingSoon.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import FeatureItem from "../../../Settings/components/FeatureItem.vue";
import log2 from "../../../../../utils/log2";
import _ from "lodash";
import StringUtils from "../../../../../utils/StringUtils";

const log = log2("ChatbotAiPrompt");
export default {
    name: "ChatbotAiPrompt",
    components: {
        FeatureItem,
        SupsisButton,
        CommingSoonData,
    },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            promptText:
                "Sen bir websitenin çağrı merkezinde çalışan müşterilere cevap veren bir canlı destek personelisin. Bilmediğin şeyler hakkında cevap verme.  Seninle websitesine gelen ziyaretciler konuşacak.",
            story: {},
            toolChoiceNone: false,
            toolChoiceAuto: true,
            toolChoiceRequired: false,
            temperature: 0.5,
            topP: 1,
            aiListenerTimeout: 10000,
            key: "test",

            Math,
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
            site: (state) => state.site.site,
        }),
    },
    mounted() {
        window.logs = [];
        const story = this.stories.find((story) => story.id === this.storyId);
        if (story.ai && !story.ai.temperature) {
            story.ai.temperature = 0.5;
        }
        if (story.ai && !story.ai.top_p) {
            story.ai.top_p = 1;
        }
        if (story.ai && !story.ai.tool_choice) {
            story.ai.tool_choice = "auto";
            this.switchToolChoice("auto");
        }
        if (story.ai && !story.ai.aiListenerTimeout) {
            story.ai.aiListenerTimeout = 10000;
        }

        if (story && story.ai && story.ai.prompt) {
            this.promptText = story.ai.prompt;
        } else {
            this.promptText = "Sen bir websitenin çağrı merkezinde çalışan müşterilere cevap veren bir canlı destek personelisin. Bilmediğin şeyler hakkında cevap verme.  Seninle websitesine gelen ziyaretciler konuşacak.";
        }

        this.story = story;
        this.temperature = story.ai?.temperature;
        this.topP = story.ai?.top_p;
        this.aiListenerTimeout = story.ai?.aiListenerTimeout || 10000;
        this.switchToolChoice(story.ai?.tool_choice);
        this.initialized = true;
        this.key = this.getKey();
    },
    methods: {
        ...mapActions({
            updateStorySingleKey: "CB/updateStorySingleKey",
        }),
        getKey() {
            return StringUtils.hashCodeObj({
                ai: this.story.ai,
                toolChoiceNone: this.toolChoiceNone,
                toolChoiceAuto: this.toolChoiceAuto,
                toolChoiceRequired: this.toolChoiceRequired,
                aiListenerTimeout: this.aiListenerTimeout,
            });
        },
        async switchToolChoice(mode) {
            log("switchToolChoice", mode);
            window.logs.push({ log: "switchToolChoice", params: { mode } });
            //await new Promise((r) => setTimeout(r, 500));
            this.toolChoiceNone = mode === "none";
            this.toolChoiceAuto = mode === "auto";
            this.toolChoiceRequired = mode === "required";
            // if (mode === "none" && this.toolChoiceNone === false) {
            //     this.toolChoiceNone = true;
            //     if (this.toolChoiceAuto === true) this.toolChoiceAuto = false;
            //     if (this.toolChoiceRequired === true) this.toolChoiceRequired = false;
            // }
            // if (mode === "auto" && this.toolChoiceAuto === false) {
            //     this.toolChoiceAuto = true;
            //     if (this.toolChoiceNone === true) this.toolChoiceNone = false;
            //     if (this.toolChoiceRequired === true) this.toolChoiceRequired = false;
            // }
            // if (mode === "required" && this.toolChoiceRequired === false) {
            //     this.toolChoiceRequired = true;
            //     if (this.toolChoiceNone === true) this.toolChoiceNone = false;
            //     if (this.toolChoiceAuto === true) this.toolChoiceAuto = false;
            // }
            this.key = this.getKey();
        },
        async updateStoryToolChoice() {
            log("updateStoryToolChoice");
            window.logs.push({ log: "updateStoryToolChoice" });
            //await new Promise((r) => setTimeout(r, 500));

            if (this.toolChoiceNone && this.story.ai.tool_choice !== "none") {
                this.story.ai.tool_choice = "none";
                this.changeToolChoice(this.story.ai.tool_choice);
            }
            if (this.toolChoiceAuto && this.story.ai.tool_choice !== "auto") {
                this.story.ai.tool_choice = "auto";
                this.changeToolChoice(this.story.ai.tool_choice);
            }
            if (this.toolChoiceRequired && this.story.ai.tool_choice !== "required") {
                this.story.ai.tool_choice = "required";
                this.changeToolChoice(this.story.ai.tool_choice);
            }
            this.key = this.getKey();

            log("updateStoryToolChoice", this.story.ai.tool_choice);
        },
        updatePrompt() {
            const prompt = this.promptText.trim();
            const aiListenerTimeout = this.aiListenerTimeout;
            this.story.ai.aiListenerTimeout = aiListenerTimeout;
            this.key = this.getKey();
            Promise.all([
                this.updateStorySingleKey({
                    id: this.story._id,
                    key: "ai.prompt",
                    value: prompt,
                }),
                this.updateStorySingleKey({
                    id: this.story._id,
                    key: "ai.aiListenerTimeout",
                    value: aiListenerTimeout,
                }),
            ]).then(() => {
                this.$message.success("Prompt updated successfully");
                sendSocketMessage("chatbot/v2 prompt-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    prompt,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
                sendSocketMessage("chatbot/v2 ai-listener-timeout-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    aiListenerTimeout: aiListenerTimeout,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
            });
        },
        clearPrompt() {
            this.promptText = "";
        },
        async changeTemperature(temperature) {
            log("changeTemperature from story", temperature);
            window.logs.push({ log: "changeTemperature from story", params: { temperature } });
            //await new Promise((r) => setTimeout(r, 500));
            this.story.ai.temperature = temperature;
            this.key = this.getKey();
            this.updateStorySingleKey({
                id: this.story._id,
                key: "ai.temperature",
                value: temperature,
            }).then(() => {
                this.$message.success("Temperature updated successfully");
                sendSocketMessage("chatbot/v2 temperature-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    temperature,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
            });
        },
        async changeTopP(topP) {
            log("changeTopP from story", topP);
            window.logs.push({ log: "changeTopP from story", params: { topP } });
            //await new Promise((r) => setTimeout(r, 500));
            this.story.ai.top_p = topP;
            this.key = this.getKey();
            this.updateStorySingleKey({
                id: this.story._id,
                key: "ai.top_p",
                value: topP,
            }).then(() => {
                this.$message.success("Top-P updated successfully");
                sendSocketMessage("chatbot/v2 top-p-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    topP,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
            });
        },
        async changeToolChoice(toolChoice) {
            log("changeToolChoice from story", toolChoice);
            window.logs.push({ log: "changeToolChoice from story", params: { toolChoice } });
            //await new Promise((r) => setTimeout(r, 500));
            this.key = this.getKey();
            this.updateStorySingleKey({
                id: this.story._id,
                key: "ai.tool_choice",
                value: toolChoice,
            }).then(() => {
                this.$message.success("Tool choice updated successfully");
                sendSocketMessage("chatbot/v2 tool-choice-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    tool_choice: toolChoice,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
            });
        },
    },
    watch: {
        toolChoiceNone: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                log("toolChoiceNone changed", newVal);
                window.logs.push({ log: "toolChoiceNone changed", params: { newVal, oldVal } });
                if (newVal && !oldVal) {
                    this.switchToolChoice("none");
                    this.updateStoryToolChoice();
                }
                this.key = this.getKey();
            },
            deep: true,
        },
        toolChoiceAuto: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                log("toolChoiceAuto changed", newVal);
                window.logs.push({ log: "toolChoiceAuto changed", params: { newVal, oldVal } });

                if (newVal === true && oldVal === false) {
                    this.switchToolChoice("auto");
                    this.updateStoryToolChoice();
                }
                this.key = this.getKey();
            },
            deep: true,
        },
        toolChoiceRequired: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                log("toolChoiceRequired changed", newVal);
                window.logs.push({ log: "toolChoiceRequired changed", params: { newVal, oldVal } });
                if (newVal && !oldVal) {
                    this.switchToolChoice("required");
                    this.updateStoryToolChoice();
                }
                this.key = this.getKey();
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss">
#ChatbotAiPrompt {
    margin: 30px;

    .settings-btn {
        display: flex;
        gap: 20px;
        float: right;
        margin: 30px;
    }

    .parameter-tuning {
        display: flex;
        flex-direction: column;
        gap: 10px;

        input {
            width: 80px;
        }

        .modes {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-top: 10px;

            .mode-items {
                display: flex;
                gap: 10px;
            }
        }
    }
}
</style>
